import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  DeleteSweep as DeleteSweepIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import authService from '../services/AuthService';
import { getApiUrl } from '../utils/env';

interface DeleteAgrarmonitorDialogProps {
  open: boolean;
  onClose: () => void;
  onDeleteComplete: () => void;
}

const DeleteAgrarmonitorDialog: React.FC<DeleteAgrarmonitorDialogProps> = ({
  open,
  onClose,
  onDeleteComplete
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResults, setDeleteResults] = useState<{
    deletedCount: number;
    totalAgrarmonitorContacts: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!isDeleting) {
      setDeleteResults(null);
      setError(null);
      onClose();
    }
  };

  const handleDeleteAgrarmonitor = async () => {
    setIsDeleting(true);
    setError(null);
    setDeleteResults(null);

    try {
      const token = authService.getAccessToken();
      if (!token) {
        throw new Error('Nicht authentifiziert');
      }

      const API_BASE_URL = getApiUrl();
      
      console.log('🗑️ DeleteAgrarmonitorDialog API Request:');
      console.log('   API_BASE_URL (verwendet):', API_BASE_URL);
      console.log('   Delete URL:', `${API_BASE_URL}/api/contacts/agrarmonitor`);
      
      const response = await fetch(`${API_BASE_URL}/api/contacts/agrarmonitor`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('🗑️ Agrarmonitor-Kontakte gelöscht:', result);
      
      setDeleteResults({
        deletedCount: result.deletedCount,
        totalAgrarmonitorContacts: result.totalAgrarmonitorContacts
      });

      // Nach erfolgreichem Löschen die Kontaktliste aktualisieren
      setTimeout(() => {
        onDeleteComplete();
      }, 2000);

    } catch (err) {
      console.error('❌ Fehler beim Löschen der Agrarmonitor-Kontakte:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler beim Löschen');
    } finally {
      setIsDeleting(false);
    }
  };

  // Nur Admins dürfen diese Funktion nutzen
  const isAdmin = authService.isAdmin();

  if (!isAdmin) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DeleteSweepIcon color="error" />
        Agrarmonitor-Kontakte löschen
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {deleteResults ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              ✅ Erfolgreich abgeschlossen!
            </Typography>
            <Typography variant="body2">
              {deleteResults.deletedCount} von {deleteResults.totalAgrarmonitorContacts} Agrarmonitor-Kontakte wurden gelöscht.
            </Typography>
          </Alert>
        ) : (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon />
                <Typography variant="h6">
                  Achtung!
                </Typography>
              </Box>
              <Typography variant="body2">
                Diese Aktion löscht <strong>alle Kontakte</strong>, die aus Agrarmonitor importiert wurden.
                Diese Aktion kann <strong>nicht rückgängig gemacht</strong> werden.
              </Typography>
            </Alert>

            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Was wird gelöscht:</strong>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                  label="Agrarmonitor-Import"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
                <Typography variant="body2">
                  Alle Kontakte mit diesem Tag
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button 
          onClick={handleClose}
          disabled={isDeleting}
        >
          {deleteResults ? 'Schließen' : 'Abbrechen'}
        </Button>
        
        {!deleteResults && (
          <Button
            onClick={handleDeleteAgrarmonitor}
            disabled={isDeleting}
            color="error"
            variant="contained"
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
          >
            {isDeleting ? 'Wird gelöscht...' : 'Alle Agrarmonitor-Kontakte löschen'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAgrarmonitorDialog;
